<template>
  <Modal
    :aria-label="addOn.name[activeLocale]"
    :show="show"
    :show-header="false"
    :wrapper-class="[
      'ticket-selection__bookable-modal with-date-options-modal lg:w-[850px]! night-based-modal max-w-full! md:max-w-[98%]! rounded-none! md:rounded-[10px]! ml-0!',
      isDark ? 'dark-theme' : '',
    ]"
    container-class="py-0! md:py-10! items-center!"
    body-class="p-6"
    @close="emit('close')"
  >
    <div class="ticket-selection__bookable-modal-header">
      <div>
        <div class="flex items-center gap-3">
          <FontAwesomeIcon :icon="['far', 'building']" class="text-lg" />
          <p class="ticket-selection__bookable-modal-title">
            {{ addOn.name[activeLocale] }}
          </p>
        </div>
        <p class="ticket-selection__bookable-modal-description">
          {{ addOn.description[activeLocale] }}
        </p>
      </div>
      <button
        type="button"
        aria-label="close icon"
        class="cursor-pointer"
        @click="emit('close')"
      >
        <FontAwesomeIcon
          :icon="['fal', 'times']"
          :class="isDark ? 'text-white/80' : 'text-[#3d4146]'"
        />
      </button>
    </div>

    <div
      :class="[
        'mt-4 flex gap-2 border p-4 rounded',
        isDark
          ? 'border-blue-400/30 bg-blue-900/30'
          : 'border-[#BEDBFF] bg-[#EFF6FF]',
      ]"
    >
      <FontAwesomeIcon
        :icon="['far', 'info-circle']"
        :class="['text-xl', isDark ? 'text-blue-400' : 'text-[#155DFC]']"
      />
      <div>
        <p
          :class="[
            'text-sm font-medium',
            isDark ? 'text-blue-300' : 'text-[#1C398E]',
          ]"
        >
          {{
            $t(
              'landingpage.checkout-page.add-ons-modal.nights-based.info.title',
            )
          }}
        </p>
        <p :class="['mt-1', isDark ? 'text-blue-300' : 'text-[#1C398E]']">
          {{
            $t(
              hasCustomAvailability
                ? 'landingpage.checkout-page.add-ons-modal.nights-based.info.custom-availability.description'
                : 'landingpage.checkout-page.add-ons-modal.nights-based.info.description',
              { price: getLowestPriceOption()?.price?.gross || 0 },
            )
          }}
        </p>
      </div>
    </div>

    <div class="mt-5">
      <DatePicker
        ref="datePickerRef"
        v-model="selectedDates"
        :time-zone="eventSettings?.timezone"
        :min-date="new Date()"
        inline
        auto-apply
        multi-calendars
        range
        :enabled-dates="enabledDates"
        :hide-offset-dates="true"
      >
        <template #day="{ day, date }">
          <div
            class="night-day-cell"
            :class="{ 'night-date-sold-out': isSoldOutDate(date) }"
          >
            <span class="night-day-date">{{ day }}</span>
            <span v-if="getPriceForDate(date) !== null" class="night-day-price">
              {{
                formatCurrencyLeft(
                  getPriceForDate(date),
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  orderDetails?.currency,
                )
              }}
            </span>
          </div>
        </template>
      </DatePicker>
    </div>

    <div
      v-if="selectedDates"
      :class="[
        'mt-5 border rounded px-6 pt-6 pb-10',
        isDark ? 'bg-white/5 border-white/10' : 'bg-[#F9FAFB] border-[#E5E7EB]',
      ]"
    >
      <div class="flex gap-2 items-center">
        <FontAwesomeIcon
          :icon="['fal', 'check']"
          class="text-xl text-[#00A63E]"
        />
        <p class="text-base!">
          {{ $t('landingpage.checkout-page.add-ons-modal.booking-summary') }}
        </p>
      </div>

      <div
        :class="[
          'mt-4 flex items-center gap-5 justify-between text-sm',
          isDark ? 'text-white/70!' : 'text-[#4A5565]!',
        ]"
      >
        <p>{{ $t('common.check-in') }}:</p>
        <p>
          {{
            selectedDates
              ? formatDate(selectedDates[0], eventSettings?.timezone)
              : ''
          }}
        </p>
      </div>
      <div
        :class="[
          'mt-2 flex items-center gap-5 justify-between text-sm',
          isDark ? 'text-white/70!' : 'text-[#4A5565]!',
        ]"
      >
        <p>{{ $t('common.check-out') }}:</p>
        <p>
          {{
            selectedDates
              ? formatDate(selectedDates[1], eventSettings?.timezone)
              : ''
          }}
        </p>
      </div>
      <div
        :class="[
          'mt-2 flex items-center gap-5 justify-between text-sm',
          isDark ? 'text-white/70!' : 'text-[#4A5565]!',
        ]"
      >
        <p>
          {{ $t('landingpage.checkout-page.add-ons-modal.number-of-nights') }}:
        </p>
        <p>
          {{ numberOfNights }}
          {{ $t(`common.${numberOfNights === 1 ? 'night' : 'nights'}`) }}
        </p>
      </div>

      <div
        :class="[
          'mt-2 pt-2 flex items-center gap-5 justify-between border-t text-lg',
          isDark ? 'border-white/10' : 'border-black/10',
        ]"
      >
        <p :class="['text-lg!', isDark ? 'text-white' : 'text-black']">
          {{ $t('landingpage.checkout-page.add-ons-modal.total-price') }}:
        </p>
        <p :class="['text-lg!', isDark ? 'text-white' : 'text-black']">
          {{
            formatCurrencyLeft(
              matchingOption?.price?.gross || 0,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 },
              orderDetails?.currency,
            )
          }}
        </p>
      </div>
    </div>

    <div class="ticket-selection__bookable-modal-footer">
      <div>
        <p class="ticket-selection__bookable-modal-price-label">
          {{ $t('landingpage.checkout-page.ticket-selection.bookables.price') }}
        </p>
        <p class="ticket-selection__bookable-modal-price-value">
          {{
            formatCurrencyLeft(
              selectedOptionValue?.price?.gross || 0,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 },
              orderDetails?.currency,
            )
          }}
        </p>
      </div>
      <div class="ticket-selection__bookable-modal-btn-container">
        <button
          type="button"
          class="ticket-selection__bookable-modal-btn-cancel btn"
          @click="emit('close')"
        >
          {{ $t('common.cancel') }}
        </button>
        <button
          type="button"
          class="ticket-selection__bookable-modal-btn-add btn"
          :disabled="!selectedOptionValue"
          @click="selectNightsAddOn"
        >
          <FontAwesomeIcon :icon="['far', 'check']" />
          {{
            $t('landingpage.checkout-page.ticket-selection.bookables.modal.add')
          }}
        </button>
      </div>
    </div>
  </Modal>
</template>

<script setup>
import { ref, inject, computed, watch } from 'vue'
import Modal from '@/Components/Modal.vue'
import { customFormat, formatDate } from '@/composables/date'
import { formatCurrencyLeft } from '@/composables/currency'
import DatePicker from '@/Components/common/DatePicker.vue'
import '@vuepic/vue-datepicker/dist/main.css'

const props = defineProps({
  show: { type: Boolean, default: () => true },
  addOn: { type: Object, default: () => {} },
})

const emit = defineEmits(['close', 'confirm'])

const activeLocale = inject('activeLocale')
const orderDetails = inject('orderDetails')
const eventSettings = inject('eventSettings')
const sectionThemeClass = inject('sectionThemeClass', ref(''))
const isDark = computed(() => sectionThemeClass.value === 'dark')

const datePickerRef = ref(null)
const selectedOptionValue = ref(null)
const selectedDates = ref(null)

const matchingOption = computed(() => {
  if (
    !selectedDates.value ||
    !selectedDates.value[0] ||
    !selectedDates.value[1]
  ) {
    return null
  }

  // Extract date portion only (YYYY-MM-DD) to avoid timezone issues
  const checkInDate = selectedDates.value[0].substring(0, 10)
  const checkOutDate = selectedDates.value[1].substring(0, 10)

  return bookableOptions.value.find((option) => {
    const optionStartDate = option.start_at.substring(0, 10)
    const optionEndDate = option.end_at.substring(0, 10)

    return optionStartDate === checkInDate && optionEndDate === checkOutDate
  })
})

watch(matchingOption, (newVal) => {
  selectedOptionValue.value = newVal
})

watch(selectedDates, (newVal) => {
  if (!newVal || !newVal[0] || !newVal[1]) return
  const checkInDate = newVal[0].substring(0, 10)
  const checkOutDate = newVal[1].substring(0, 10)
  const isBookable = bookableOptions.value.some((option) => {
    return (
      option.start_at.substring(0, 10) === checkInDate &&
      option.end_at.substring(0, 10) === checkOutDate
    )
  })
  if (!isBookable) {
    datePickerRef.value?.clear()
  }
})

const soldOutDates = computed(() => {
  return props.addOn.availability_per_night
    .filter((item) => item.capacity.remaining === 0)
    .map((item) => item.date)
})

const isSoldOutDate = (date) => {
  const dateKey = customFormat(date, 'yyyy-MM-dd')
  if (dateKey < today.value) return false
  return soldOutDates.value.includes(dateKey)
}

const today = computed(() => customFormat(new Date(), 'yyyy-MM-dd'))

const bookableOptions = computed(() => {
  const perNight = props.addOn.availability_per_night
  const sold = soldOutDates.value
  return (props.addOn.options || []).filter((option) => {
    const startDate = option.start_at?.substring(0, 10)
    const endDate = option.end_at?.substring(0, 10)
    if (!startDate || !endDate) return false
    if (startDate < today.value) return false
    const nights = perNight.filter(
      (n) => n.date >= startDate && n.date < endDate,
    )
    return nights.length > 0 && nights.every((n) => !sold.includes(n.date))
  })
})

const enabledDates = computed(() => {
  const dates = new Set()

  // Night dates with capacity from availability_per_night
  for (const night of props.addOn.availability_per_night) {
    if (night.date >= today.value && !soldOutDates.value.includes(night.date)) {
      dates.add(night.date)
    }
  }

  // Check-out dates from bookable options
  for (const option of bookableOptions.value) {
    const endDate = option.end_at?.substring(0, 10)
    if (endDate && endDate >= today.value) {
      dates.add(endDate)
    }
  }

  return [...dates]
})

const datePriceMap = computed(() => {
  const map = {}
  for (const night of props.addOn.availability_per_night) {
    map[night.date] = night.price.gross
  }
  return map
})

const getPriceForDate = (date) => {
  const dateKey = customFormat(date, 'yyyy-MM-dd')
  return datePriceMap.value[dateKey] ?? null
}

const numberOfNights = computed(() => {
  if (
    !selectedDates.value ||
    !selectedDates.value[0] ||
    !selectedDates.value[1]
  ) {
    return 0
  }
  const checkIn = new Date(selectedDates.value[0])
  const checkOut = new Date(selectedDates.value[1])
  const diffTime = checkOut.getTime() - checkIn.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
})

const hasCustomAvailability = computed(() => {
  const nights = props.addOn.availability_per_night
  if (!nights || nights.length === 0) {
    return false
  }
  const firstPrice = nights[0].price.gross
  return nights.some((night) => night.price.gross !== firstPrice)
})

const selectNightsAddOn = () => {
  emit('confirm', selectedOptionValue.value)
}

const getLowestPriceOption = () => {
  if (!props.addOn.options?.length) return null
  return props.addOn.options.reduce((lowest, option) => {
    if (!lowest || option.price?.gross < lowest.price?.gross) {
      return option
    }
    return lowest
  }, null)
}
</script>
